import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthShell } from '@/components/AuthShell';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { ageFromBirthdate, MIN_SIGNUP_AGE } from '@gobingebox/shared';
import { ApiError } from '@/lib/api';
import { authApi } from '@/features/auth/api';
import { useRefreshViewer } from '@/features/auth/AuthContext';

export function SignupPage() {
  const navigate = useNavigate();
  const refreshViewer = useRefreshViewer();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!birthdate || ageFromBirthdate(birthdate) < MIN_SIGNUP_AGE) {
      setError(`You must be at least ${MIN_SIGNUP_AGE} to create an account.`);
      return;
    }
    setBusy(true);
    try {
      await authApi.signup({ email, password, displayName, birthdate });
      await refreshViewer();
      navigate('/browse', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.body.error === 'email_taken') {
        setError('That email is already registered. Try signing in.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Browse and watch previews free — subscribe any time to unlock full episodes."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-brand-soft hover:text-brand">
            Sign in
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {error && <Alert tone="error">{error}</Alert>}
        <Field
          label="Display name"
          autoComplete="nickname"
          required
          maxLength={60}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Field
          label="Date of birth"
          type="date"
          autoComplete="bday"
          required
          hint={`You must be at least ${MIN_SIGNUP_AGE}.`}
          value={birthdate}
          onChange={(e) => setBirthdate(e.target.value)}
        />
        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthShell>
  );
}
